import React, { useMemo } from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Register Roboto fonts
Font.register({
  family: 'Roboto',
  fonts: [
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-VariableFont_wdth,wght.ttf`,
      fontWeight: 'normal'
    },
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-Italic-VariableFont_wdth,wght.ttf`,
      fontStyle: 'italic',
      fontWeight: 'normal'
    }
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontFamily: 'Roboto',
    fontSize: 8,
    position: 'relative',
    paddingBottom: 60
  },
  header: {
    marginBottom: 6,
    borderBottom: '1px solid #000',
    paddingBottom: 3
  },
  title: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 3,
    textAlign: 'center'
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2
  },
  section: {
    marginBottom: 4
  },
  categoryHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 2,
    borderBottom: '1px solid #000',
    paddingBottom: 2
  },
  locationHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 2,
    paddingTop: 1
  },
  projectHeader: {
    fontSize: 8,
    fontStyle: 'italic',
    marginBottom: 1,
    paddingBottom: 1
  },
  locationContainer: {
    marginBottom: 2
  },
  quantityTotal: {
    fontSize: 8,
    fontWeight: 'normal',
    fontStyle: 'italic',
    textAlign: 'right',
    marginBottom: 0
  },
  table: {
    marginBottom: 4
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingVertical: 2,
    alignItems: 'center'
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#999',
    fontWeight: 'bold'
  },
  tableCell: {
    padding: 1,
    fontSize: 8,
    textAlign: 'left'
  },
  serialCell: {
    width: '5%'
  },
  itemNameCell: {
    width: '28%'
  },
  unitCell: {
    width: '3%'
  },
  quantityCell: {
    width: '9%',
    textAlign: 'right'
  },
  stockCell: {
    width: '8%',
    textAlign: 'right'
  },
  dateCell: {
    width: '13%',
    textAlign: 'right'
  },
  indentCell: {
    width: '9%',
    textAlign: 'right'
  },
  grandTotalSection: {
    marginTop: 5,
    borderTop: '2px solid #000',
    paddingTop: 3
  },
  grandTotalRow: {
    flexDirection: 'row',
    paddingVertical: 2,
    justifyContent: 'space-between'
  },
  footerSection: {
    position: 'absolute',
    bottom: 30,
    left: 15,
    right: 15,
    textAlign: 'left',
    fontSize: 7,
    color: '#666',
    borderTop: '1px solid #ddd',
    paddingTop: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  pageNumber: {
    fontSize: 8,
    color: '#666'
  },
  pageContent: {
    flex: 1,
    marginBottom: 45
  }
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
};

const TableHeader = () => (
  <View style={[styles.tableRow, styles.tableHeader]}>
    <Text style={[styles.tableCell, styles.serialCell]}>S.No</Text>
    <Text style={[styles.tableCell, styles.itemNameCell]}>Item Name</Text>
    <Text style={[styles.tableCell, styles.unitCell]}>Unit</Text>
    <Text style={[styles.tableCell, styles.quantityCell]}>Quantity</Text>
    <Text style={[styles.tableCell, styles.stockCell]}>Stock</Text>
    <Text style={[styles.tableCell, styles.dateCell]}>Committed Days</Text>
    <Text style={[styles.tableCell, styles.indentCell]}>Indent No.</Text>
  </View>
);

// Fixed PageWrapper component to ensure page numbers appear on every page
const PageWrapper = ({ children }) => (
  <Page size="A4" style={styles.page}>
    <View style={styles.pageContent}>{children}</View>
    <View style={styles.footerSection} fixed>
      <Text>Generated on {formatDate(new Date())} • Indent Management System</Text>
      <Text
        style={styles.pageNumber}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  </Page>
);

const BatchPDF = ({ batch, data, selectedCategories = [] }) => {
  const processedData = useMemo(() => {
    if (!data || !selectedCategories || selectedCategories.length === 0) {
      return { groupedByCategory: {}, totalQuantity: 0 };
    }
    const groupedByCategory = {};
    let totalQuantity = 0;
    Object.entries(data).forEach(([location, projects]) => {
      Object.entries(projects).forEach(([project, categories]) => {
        Object.entries(categories).forEach(([category, items]) => {
          if (selectedCategories.includes(category)) {
            if (!groupedByCategory[category]) groupedByCategory[category] = {};
            if (!groupedByCategory[category][location]) groupedByCategory[category][location] = {};
            if (!groupedByCategory[category][location][project]) groupedByCategory[category][location][project] = [];
            groupedByCategory[category][location][project] = items.map(item => ({
              ItemName: item.ItemName,
              UnitName: item.UnitName,
              Quantity: item.Quantity,
              Stock: item.Stock,
              CommitedDays: item.CommitedDays,
              CommitedDate: item.CommitedDate,
              IndentNumber: item.IndentNumber
            })).sort((a, b) => {
              // Natural sort function for mixed alphanumeric strings
              const naturalSort = (a, b) => {
                const ax = [], bx = [];
                
                a.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
                  ax.push([$1 || Infinity, $2 || ""]);
                });
                b.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
                  bx.push([$1 || Infinity, $2 || ""]);
                });
                
                while (ax.length && bx.length) {
                  const an = ax.shift();
                  const bn = bx.shift();
                  const nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
                  if (nn) return nn;
                }
                
                return ax.length - bx.length;
              };
              
              return naturalSort(a.ItemName, b.ItemName);
            });
            items.forEach(item => {
              totalQuantity += Number(item.Quantity) || 0;
            });
          }
        });
      });
    });
    return { groupedByCategory, totalQuantity };
  }, [data, selectedCategories]);

  const { groupedByCategory, totalQuantity } = processedData;

  if (!data || !selectedCategories || selectedCategories.length === 0) {
    return (
      <Document>
        <PageWrapper>
          <Text>No data available for export</Text>
        </PageWrapper>
      </Document>
    );
  }

  // Global serial number to be continuous across entire document
  let globalSerialNumber = 1;

  return (
    <Document>
      <PageWrapper>
        <View style={styles.header}>
          <Text style={styles.title}>{batch.Description}</Text>
          <View style={styles.infoRow}>
            <Text>Batch Number: {batch.BatchNumber}</Text>
            <Text>Created At: {formatDate(batch.CreatedAt)}</Text>
          </View>
        </View>
        {Object.entries(groupedByCategory).map(([category, locations], categoryIndex) => {
          let categoryTotalQuantity = 0;
          let allLocationItems = [];
          Object.entries(locations).forEach(([location, projects]) => {
            Object.entries(projects).forEach(([project, items]) => {
              let locationProjectQuantity = 0;
              items.forEach(item => {
                locationProjectQuantity += Number(item.Quantity) || 0;
              });
              categoryTotalQuantity += locationProjectQuantity;
              allLocationItems.push({
                location,
                project,
                items,
                totalQuantity: locationProjectQuantity
              });
            });
          });
          return (
            <View key={category} style={styles.section}>
              <View style={styles.categoryHeader}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>{category}</Text>
                  <Text style={styles.quantityTotal}>Total Quantity: {categoryTotalQuantity}</Text>
                </View>
              </View>
              {/* Show table header only once before the first category */}
              {categoryIndex === 0 && (
                <View style={styles.table}>
                  <TableHeader />
                </View>
              )}
              {allLocationItems.map(({ location, project, items, totalQuantity }, locIndex) => {
                return (
                  <React.Fragment key={`${category}-${location}-${project}`}>
                    <View style={styles.locationContainer}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={styles.locationHeader}>{location} - {project}</Text>
                        <Text style={styles.quantityTotal}>Total Quantity: {totalQuantity}</Text>
                      </View>
                    </View>
                    {items.map((item, index) => (
                      <View key={index} style={styles.tableRow}>
                        <Text style={[styles.tableCell, styles.serialCell]}>{globalSerialNumber++}</Text>
                        <Text style={[styles.tableCell, styles.itemNameCell]}>{item.ItemName}</Text>
                        <Text style={[styles.tableCell, styles.unitCell]}>{item.UnitName}</Text>
                        <Text style={[styles.tableCell, styles.quantityCell]}>{item.Quantity}</Text>
                        <Text style={[styles.tableCell, styles.stockCell]}>{item.Stock}</Text>
                        <Text style={[styles.tableCell, styles.dateCell]}>
                        {item.CommitedDays ? `${item.CommitedDays} days` : 'N/A'}
                        </Text>
                        <Text style={[styles.tableCell, styles.indentCell]}>{item.IndentNumber || 'N/A'}</Text>
                      </View>
                    ))}
                  </React.Fragment>
                );
              })}
            </View>
          );
        })}
        <View style={styles.grandTotalSection}>
          <View style={styles.grandTotalRow}>
            <Text style={{ fontSize: 9, fontWeight: 'bold', padding: 2 }}>Grand Total:</Text>
            <Text style={{ fontSize: 9, fontWeight: 'bold', padding: 2 }}>Total Quantity: {totalQuantity}</Text>
          </View>
        </View>
      </PageWrapper>
    </Document>
  );
};

export default BatchPDF;